import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * HeroCoach — chat-first AI financial coach overlay.
 *
 * Floating button bottom-right of the dashboard. Tap → full-height chat sheet
 * slides up. Messages persist server-side per session_id ("default").
 */
const SESSION_ID = "default";

const SUGGESTED_OPENERS = [
  "What's my biggest money leak right now?",
  "Where should I focus this week?",
  "How do I build a $1,000 emergency fund?",
  "Can I afford to save $200 a month?",
];

const GREETING_DISMISS_KEY = "heroCoachGreetingDismissedAt";
const GREETING_TTL_MS = 6 * 60 * 60 * 1000; // re-surface every 6 hours

export default function HeroCoach() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [greeting, setGreeting] = useState(null); // { tip, cta } or null
  const listRef = useRef(null);

  // Fetch a personalized greeting once per session (respecting dismiss TTL).
  useEffect(() => {
    if (open) return;
    try {
      const last = Number(localStorage.getItem(GREETING_DISMISS_KEY) || 0);
      if (last && Date.now() - last < GREETING_TTL_MS) return;
    } catch { /* ignore */ }
    let cancelled = false;
    api.get("/coach/greeting")
      .then((res) => { if (!cancelled && res.data?.tip) setGreeting(res.data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [open]);

  const dismissGreeting = () => {
    setGreeting(null);
    try { localStorage.setItem(GREETING_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const acceptGreeting = () => {
    const cta = greeting?.cta;
    dismissGreeting();
    setOpen(true);
    if (cta) setTimeout(() => send(cta), 250);
  };

  useEffect(() => {
    if (!open) return;
    setHydrating(true);
    api.get(`/coach/messages?session_id=${SESSION_ID}`)
      .then((res) => setMessages(res.data.messages || []))
      .catch(() => { /* show empty */ })
      .finally(() => setHydrating(false));
  }, [open]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if (!text || sending) return;
    setDraft("");
    setSending(true);
    const userTurn = { role: "user", text, ts: new Date().toISOString(), id: `tmp-${Date.now()}` };
    setMessages((prev) => [...prev, userTurn]);
    try {
      const res = await api.post("/coach/chat", { session_id: SESSION_ID, message: text });
      const aiTurn = {
        role: "assistant",
        text: res.data.reply,
        suggestions: res.data.suggestions || [],
        ts: new Date().toISOString(),
        id: `ai-${Date.now()}`,
      };
      setMessages((prev) => [...prev, aiTurn]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Coach is offline. Try again.");
      setMessages((prev) => prev.filter((m) => m.id !== userTurn.id));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {greeting && !open && (
        <div
          className="fixed bottom-24 right-5 z-40 max-w-[300px] sm:max-w-xs animate-in fade-in slide-in-from-bottom-2 duration-300"
          data-testid="coach-greeting"
        >
          <div className="relative bg-[#06190F] border border-[#1B4238] rounded-2xl shadow-2xl shadow-emerald-900/40 p-3.5 pr-8">
            <button
              type="button"
              onClick={dismissGreeting}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full text-[#7B948B] hover:text-white hover:bg-[#11413B] flex items-center justify-center"
              aria-label="Dismiss coach greeting"
              data-testid="coach-greeting-dismiss"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2.6} />
            </button>
            <p className="text-[13px] text-[#E1EEE8] leading-relaxed">{greeting.tip}</p>
            {greeting.cta && (
              <button
                type="button"
                onClick={acceptGreeting}
                className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] px-3 py-1.5 rounded-full transition-colors"
                data-testid="coach-greeting-cta"
              >
                <Sparkles className="w-3 h-3" strokeWidth={2.8} /> {greeting.cta}
              </button>
            )}
            <div
              className="absolute -bottom-1.5 right-8 w-3 h-3 bg-[#06190F] border-r border-b border-[#1B4238] rotate-45"
              aria-hidden="true"
            />
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => { dismissGreeting(); setOpen(true); }}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 h-14 px-5 rounded-full bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-extrabold uppercase tracking-wider text-sm shadow-2xl shadow-emerald-500/30 transition-transform hover:scale-105 active:scale-95"
        style={{ fontFamily: "Outfit, sans-serif" }}
        data-testid="coach-launcher"
        aria-label="Open Hero Coach"
      >
        <Sparkles className="w-5 h-5" strokeWidth={2.8} />
        Ask Hero
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="coach-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="hero-theme w-full sm:max-w-md h-[88vh] sm:h-[80vh] bg-[#06190F] rounded-t-3xl sm:rounded-3xl border border-[#1B4238] shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1B4238] bg-gradient-to-b from-[#0C2A1D] to-transparent">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[#22E07A] flex items-center justify-center text-[#05160D]">
                  <Sparkles className="w-5 h-5" strokeWidth={2.8} />
                </div>
                <div>
                  <p className="text-white font-extrabold text-base leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>Hero Coach</p>
                  <p className="text-[11px] text-[#7B948B] mt-0.5">Your AI money sidekick</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-[#11413B] text-[#C7DAD3] flex items-center justify-center"
                data-testid="coach-close"
                aria-label="Close coach"
              >
                <X className="w-5 h-5" strokeWidth={2.4} />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="coach-messages">
              {hydrating && (
                <div className="flex items-center justify-center py-8 text-[#7B948B]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              )}
              {!hydrating && messages.length === 0 && (
                <div className="space-y-3 pt-4">
                  <p className="text-[#C7DAD3] text-sm leading-relaxed">
                    Hey — I'm Hero Coach. I see your subscriptions, your Hero Score, and your wins. Ask me anything about your money. 💪
                  </p>
                  <div className="space-y-2 pt-3">
                    <p className="text-[10px] uppercase tracking-wider text-[#7B948B] font-bold">Try asking</p>
                    {SUGGESTED_OPENERS.map((q) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        className="w-full text-left text-sm text-[#C7DAD3] bg-[#0E2924] hover:bg-[#11413B] border border-[#1B4238] rounded-2xl px-3.5 py-2.5 transition-colors"
                        data-testid={`coach-opener-${q.slice(0, 20)}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m) => (
                <CoachBubble key={m.id || m.ts} msg={m} onSuggestion={send} />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-[#7B948B] text-xs px-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Hero Coach is thinking…
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-[#1B4238] bg-[#06190F] px-3 py-3 flex items-end gap-2"
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder="Ask about your money…"
                disabled={sending}
                className="flex-1 resize-none max-h-32 min-h-[44px] bg-[#0A2320] text-white border border-[#1B4238] rounded-2xl px-3.5 py-2.5 text-sm outline-none focus:border-[#22E07A]"
                data-testid="coach-input"
              />
              <button
                type="submit"
                disabled={!draft.trim() || sending}
                className="h-11 w-11 rounded-2xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="coach-send"
                aria-label="Send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2.8} />}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function CoachBubble({ msg, onSuggestion }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#22E07A] text-[#05160D] font-medium rounded-br-sm"
            : "bg-[#0E2924] text-[#E1EEE8] border border-[#1B4238] rounded-bl-sm"
        }`}
        data-testid={`coach-bubble-${msg.role}`}
      >
        {msg.text}
        {!isUser && msg.suggestions?.length > 0 && (
          <div className="mt-2 -mx-1 flex flex-wrap gap-1.5">
            {msg.suggestions.map((s) => (
              <button
                key={s}
                onClick={() => onSuggestion(s)}
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-[#22E07A]/15 hover:bg-[#22E07A]/25 text-[#22E07A] border border-[#22E07A]/40 px-2.5 py-1 rounded-full transition-colors"
              >
                <MessageCircle className="w-3 h-3" strokeWidth={2.6} /> {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
